import type { User } from '../api/types';

/**
 * A pointer to one drillable record. DrilldownProvider holds a stack of these
 * and DrilldownHost switches on `kind` to pick the modal.
 *
 * `store` carries the row's date on purpose: StoreDrilldownModal fetches a
 * single day of sales and defaults to today, so a March report row opened
 * without a date would show today's numbers under a March heading.
 */
export type DrillRef =
  /**
   * `invoice: false` suppresses the CFDI download buttons for a ticket that
   * has no invoice behind it — an inventory transfer, say. Omitted, the host
   * derives it from the user's role, matching what the pages did inline.
   * It is presentation, not identity, so drillRefEquals ignores it.
   */
  | { kind: 'tkt';     sid: number; tnum: number; invoice?: boolean }
  | { kind: 'account'; anum: number }
  | { kind: 'order';   sid: number; onum: number }
  | { kind: 'inv';     sid: number; line: string; part: string }
  | { kind: 'store';   sid: number; date?: string; storeName?: string };

export type DrillKind = DrillRef['kind'];

/**
 * Mirrors what the Go handlers already enforce (view:sales on /api/ticket and
 * /api/sales, view:customer.detail on /api/customer, view:vendors on the
 * purchasing routes, view:part.detail on /api/part and /api/inv-record).
 * This is the UX layer — the server is the enforcement.
 */
const PERM_FOR_KIND: Record<DrillKind, string> = {
  tkt: 'view:sales',
  account: 'view:customer.detail',
  order: 'view:vendors',
  inv: 'view:part.detail',
  store: 'view:sales',
};

export function canDrill(user: User | null | undefined, kind: DrillKind): boolean {
  if (!user) return false;
  return user.role === 'owner' || !!user.permissions?.includes(PERM_FOR_KIND[kind]);
}

/** Structural equality, so open() can ignore a double-click on the same cell. */
export function drillRefEquals(a: DrillRef | null, b: DrillRef | null): boolean {
  if (!a || !b || a.kind !== b.kind) return false;
  switch (a.kind) {
    case 'tkt':     return b.kind === 'tkt' && a.sid === b.sid && a.tnum === b.tnum;
    case 'account': return b.kind === 'account' && a.anum === b.anum;
    case 'order':   return b.kind === 'order' && a.sid === b.sid && a.onum === b.onum;
    case 'inv':     return b.kind === 'inv' && a.sid === b.sid && a.line === b.line && a.part === b.part;
    case 'store':   return b.kind === 'store' && a.sid === b.sid && a.date === b.date;
  }
}

/**
 * A positive identifier, or null. Report rows arrive as daemon JSON where a
 * numeric column can come back as a string, so both are accepted.
 */
function num(v: unknown): number | null {
  const n = typeof v === 'number' ? v : typeof v === 'string' ? Number(v) : NaN;
  return Number.isFinite(n) && n > 0 ? n : null;
}

/**
 * A non-empty trimmed string, or null. Progress CHARACTER fields are
 * space-padded; an untrimmed part would miss the daemon's keyed FIND.
 */
function str(v: unknown): string | null {
  const s = typeof v === 'string' ? v.trim() : '';
  return s === '' ? null : s;
}

function rowDate(row: Record<string, unknown>): string | undefined {
  return str(row.idate) ?? str(row.cdate) ?? undefined;
}

/**
 * Maps one report/table cell to the record it points at, or null when the row
 * lacks the companion fields the ref needs.
 *
 * Returning null is the point: a report whose columns omit `sid` renders plain
 * text instead of guessing a store, and DrillCell shows nothing clickable.
 */
export function refForCell(col: string, row: Record<string, unknown>): DrillRef | null {
  const sid = num(row.sid);
  switch (col) {
    case 'tnum': {
      const tnum = num(row.tnum);
      return sid && tnum ? { kind: 'tkt', sid, tnum } : null;
    }
    case 'anum': {
      const anum = num(row.anum);
      return anum ? { kind: 'account', anum } : null;
    }
    case 'onum': {
      const onum = num(row.onum);
      return sid && onum ? { kind: 'order', sid, onum } : null;
    }
    case 'part': {
      const line = str(row.line);
      const part = str(row.part);
      return sid && line && part ? { kind: 'inv', sid, line, part } : null;
    }
    case 'sid': {
      if (!sid) return null;
      const date = rowDate(row);
      return date ? { kind: 'store', sid, date } : { kind: 'store', sid };
    }
    default:
      return null;
  }
}
